import React, { useContext, useEffect, useState } from "react";
import {
  Form,
  Button,
  Input,
  Select,
  Radio,
  Upload,
  message,
  Modal,
  Row,
  Col,
  InputNumber,
  Popconfirm,
  Checkbox,
  Space,
  Tag,
  Spin,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  activateCompany,
  deactivateCompany,
  getCities,
  getRegions,
  updateCompanyDetails,
  deleteCompany,
  deleteUpload,
  getBusinessTypes,
} from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import {
  LockOutlined,
  UnlockOutlined,
  EyeOutlined,
  UploadOutlined,
  LoadingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  COMPANY_STATUS_KYC,
  USER_TYPE_ADMIN_STAFF,
} from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import { useNavigate } from "react-router-dom";
import UpdateReseller from "./UpdateReseller";
import UpdateCompanyAdminStaff from "./UpdateCompanyAdminStaff";
import {
  buildFormFieldErrors,
  extractApiErrorMessages,
  getApiResponseData,
  isSuccessfulApiResponse,
} from "../../../utils/apiErrors";
import {
  buildCompanyDetailsPayload,
  getCompanyAddressFormValues,
  hasCompleteCompanyBusinessAddress,
} from "../../../utils/addressContracts";

const COMPANY_DETAILS_FORM_FIELDS = [
  "business_name",
  "subdomain",
  "business_arabic_name",
  "business_type_id",
  "business_type_verified",
  "cr",
  "is_vat_exempt",
  "vat",
  "round_off",
  "is_calories",
  "business_country",
  "business_postal_code",
  "business_region_id",
  "business_city_id",
  "business_address",
];

const EMPTY_VALUE = "—";
const TENANT_BASE_DOMAIN = (
  process.env.REACT_APP_TENANT_BASE_DOMAIN || ""
).trim();

const CustomerInfo = ({ company, reloadCallback }) => {
  const { authUser, appConfig } = useContext(AppContext);

  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);

  const [crCertificateUrl, setCRCertificateUrl] = useState("");
  const [crCertificateUploading, setCRCertificateUploading] = useState(false);

  const [vatCertificateUrl, setVATCertificateUrl] = useState("");
  const [vatCertificateUploading, setVATCertificateUploading] = useState(false);

  const [crVerificationUrl, setCRVerificationUrl] = useState("");
  const [crVerificationUploading, setCRVerificationUploading] = useState(false);

  const [vatVerificationUrl, setVATVerificationUrl] = useState("");
  const [vatVerificationUploading, setVATVerificationUploading] =
    useState(false);

  const [editModal, setEditModal] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [vatExempt, setVatExempt] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);

  const [storeConfigurationForm] = Form.useForm();
  const subdomain = Form.useWatch("subdomain", storeConfigurationForm);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const region_id = company.business_region?.id;

  useEffect(() => {
    getRegions().then((response) => {
      setRegions(response.data.data.regions);
    });
  }, []);

  useEffect(() => {
    getBusinessTypes().then((response) => {
      setBusinessTypes(response.data.data);
    });
  }, []);

  const showEditModel = () => {
    setLogoUrl(company.logo);
    setCRCertificateUrl(company.cr_certificate);
    setVATCertificateUrl(company.vat_certificate);
    setCRVerificationUrl(company.cr_verification);
    setVATVerificationUrl(company.vat_verification);

    const isVatExempt = !!company.is_vat_exempt;

    storeConfigurationForm.resetFields();

    // Set form initial values
    storeConfigurationForm.setFieldsValue({
      id: company.id,
      logo: company.logo,
      business_name: company.name,
      subdomain: company.subdomain,
      business_type_id: company.business_type?.id,
      cr: company.cr,
      cr_certificate: company.cr_certificate,
      cr_verification: company.cr_verification,
      is_vat_exempt: isVatExempt,
      vat: company.vat,
      vat_certificate: company.vat_certificate,
      vat_verification: company.vat_verification,
      ...getCompanyAddressFormValues(company),
      round_off: company.round_off,
      is_calories: company.is_calories,
      business_type_verified: company.business_type_verified,
    });

    setVatExempt(isVatExempt);
    handleCityRequest(region_id);
    setEditModal(true);
  };

  const showCompanyUpdateErrors = (response) => {
    const responseData = getApiResponseData(response);
    const errorMessages = extractApiErrorMessages(responseData?.data?.errors);

    if (errorMessages.length > 0) {
      storeConfigurationForm.setFields(
        buildFormFieldErrors(errorMessages, COMPANY_DETAILS_FORM_FIELDS),
      );
      errorMessages.forEach(({ message: errorMessage }) => {
        NotificationWithIcon("error", t("something_went_wrong"), errorMessage);
      });
      return;
    }

    NotificationWithIcon(
      "error",
      t("something_went_wrong"),
      responseData?.message || t("request_failed"),
    );
  };

  const uploadCustomRequest = (data, file_name) => {
    const formData = new FormData();

    if (file_name === "logo") {
      formData.append("logo", data.file);
    } else if (file_name === "cr_certificate") {
      formData.append("cr_certificate", data.file);
    } else if (file_name === "vat_certificate") {
      formData.append("vat_certificate", data.file);
    } else if (file_name === "cr_verification") {
      formData.append("cr_verification", data.file);
    } else if (file_name === "vat_verification") {
      formData.append("vat_verification", data.file);
    }

    updateCompanyDetails(company.id, formData)
      .then((response) => {
        const responseData = getApiResponseData(response);

        if (isSuccessfulApiResponse(response)) {
          if (file_name === "logo") {
            setLogoUrl(responseData.data.user.logo);
          } else if (file_name === "cr_certificate") {
            setCRCertificateUrl(responseData.data.user.cr_certificate);
          } else if (file_name === "vat_certificate") {
            setVATCertificateUrl(responseData.data.user.vat_certificate);
          } else if (file_name === "cr_verification") {
            setCRVerificationUrl(responseData.data.user.cr_verification);
          } else if (file_name === "vat_verification") {
            setVATVerificationUrl(responseData.data.user.vat_verification);
          }
          reloadCallback();
        } else {
          showCompanyUpdateErrors(response);
        }
      })
      .catch((error) => {
        showCompanyUpdateErrors(error);
      })
      .finally(() => {
        if (file_name === "logo") {
          setLogoUploading(false);
        } else if (file_name === "cr_certificate") {
          setCRCertificateUploading(false);
        } else if (file_name === "vat_certificate") {
          setVATCertificateUploading(false);
        } else if (file_name === "cr_verification") {
          setCRVerificationUploading(false);
        } else if (file_name === "vat_verification") {
          setVATVerificationUploading(false);
        }
      });
  };

  const handleChange = (info, file_name) => {
    if (info.file.status === "uploading" && file_name === "logo") {
      setLogoUploading(true);
      setLogoUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "cr_certificate"
    ) {
      setCRCertificateUploading(true);
      setCRCertificateUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "vat_certificate"
    ) {
      setVATCertificateUploading(true);
      setVATCertificateUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "cr_verification"
    ) {
      setCRVerificationUploading(true);
      setCRVerificationUrl("");
    } else if (
      info.file.status === "uploading" &&
      file_name === "vat_verification"
    ) {
      setVATVerificationUploading(true);
      setVATVerificationUrl("");
    }
    return;
  };

  // check the logo before uploading
  function beforeUpload(file) {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error(t("validate_image_file_type"));
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t("validate_image_size_2"));
    }
    return isJpgOrPng && isLt2M;
  }

  // check the cr and vat certificate and verification before uploading
  function beforeUploadCertificate(file) {
    const isJpgOrPngOrPdf =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "application/pdf";
    if (!isJpgOrPngOrPdf) {
      message.error(t("validate_image_pdf_file_type"));
    }
    const isLt8M = file.size / 1024 / 1024 < 8;
    if (!isLt8M) {
      message.error(t("validate_image_size_8"));
    }
    return isJpgOrPngOrPdf && isLt8M;
  }

  const handleActivate = (company_id) => {
    activateCompany(company_id).then((response) => {
      if (response.data.success === true) {
        NotificationWithIcon(
          "success",
          t("company_activated"),
          t("company_activated_successfully"),
        );
        reloadCallback();
      } else {
        NotificationWithIcon(
          "error",
          t("something_went_wrong"),
          response.data.message,
        );
      }
    });
  };

  const handleDeactivate = (company_id) => {
    deactivateCompany(company_id).then((response) => {
      if (response.data.success === true) {
        NotificationWithIcon(
          "success",
          t("company_deactivated"),
          t("company_deactivated_successfully"),
        );
        reloadCallback();
      } else {
        NotificationWithIcon(
          "error",
          t("something_went_wrong"),
          response.data.message,
        );
      }
    });
  };

  // send request on finish
  const onFinishConfigForm = () => {
    if (!company.cr_certificate && !crCertificateUrl) {
      message.error(t("validate_cr_certificate_required"));
    } else if (!vatExempt && !company.vat_certificate && !vatCertificateUrl) {
      message.error(t("validate_vat_certificate_required"));
    } else {
      storeConfigurationForm.validateFields().then((formData) => {
        setLoading(true);
        updateCompanyDetails(company.id, buildCompanyDetailsPayload(formData))
          .then((response) => {
            if (isSuccessfulApiResponse(response)) {
              NotificationWithIcon(
                "success",
                t("company_updated"),
                t("company_updated_successfully"),
              );
              setEditModal(false);
              reloadCallback();
            } else {
              showCompanyUpdateErrors(response);
            }
          })
          .catch((error) => {
            showCompanyUpdateErrors(error);
          })
          .finally(() => {
            setLoading(false);
          });
      });
    }
  };

  const uploadButton = (file_name) => (
    <div>
      {file_name === "logo" && logoUploading && <LoadingOutlined />}
      {file_name === "logo" && !logoUploading && <UploadOutlined />}

      {file_name === "cr_certificate" && crCertificateUploading && (
        <LoadingOutlined />
      )}
      {file_name === "cr_certificate" && !crCertificateUploading && (
        <UploadOutlined />
      )}

      {file_name === "vat_certificate" && vatCertificateUploading && (
        <LoadingOutlined />
      )}
      {file_name === "vat_certificate" && !vatCertificateUploading && (
        <UploadOutlined />
      )}

      {file_name === "cr_verification" && crVerificationUploading && (
        <LoadingOutlined />
      )}
      {file_name === "cr_verification" && !crVerificationUploading && (
        <UploadOutlined />
      )}

      {file_name === "vat_verification" && vatVerificationUploading && (
        <LoadingOutlined />
      )}
      {file_name === "vat_verification" && !vatVerificationUploading && (
        <UploadOutlined />
      )}

      <div style={{ marginTop: 8 }}>{t("upload")}</div>
    </div>
  );

  const handleCityRequest = (value) => {
    if (value) {
      setLoading(true);
      getCities(value)
        .then((response) => {
          setCities(response.data.data.cities);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setCities([]);
    }
  };

  const handleRegionSelect = (value) => {
    storeConfigurationForm.resetFields(["business_city_id"]);
    setCities([]); // optional: clear previous cities
    if (!value) return;

    setLoading(true);
    getCities(value)
      .then((response) => {
        setCities(response.data.data.cities);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const requestReloadCallback = () => {
    reloadCallback();
  };

  const handleDeleteCompany = (id) => {
    deleteCompany(id)
      .then((response) => {
        if (response.data.success === true) {
          NotificationWithIcon(
            "success",
            t("company_deleted"),
            t("company_deleted_successfully"),
          );
          navigate("/companies");
        } else {
          NotificationWithIcon(
            "error",
            t("something_went_wrong"),
            response.data.message,
          );
        }
      })
      .catch((info) => {
        console.log("API Failed:", info.response);
        NotificationWithIcon(
          "error",
          t("something_went_wrong"),
          info.response.data.data.errors.name,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteUpload = (id, type) => {
    const formData = new FormData();
    formData.append("file_type", type);
    deleteUpload(id, formData)
      .then((response) => {
        if (response.data.success === true) {
          reloadCallback();
          NotificationWithIcon(
            "success",
            t("upload_deleted"),
            t("upload_deleted_successfully"),
          );
          if (type === "logo") {
            setLogoUrl("");
          } else if (type === "cr_certificate") {
            setCRCertificateUrl("");
          } else if (type === "vat_certificate") {
            setVATCertificateUrl("");
          } else if (type === "cr_verification") {
            setCRVerificationUrl("");
          } else if (type === "vat_verification") {
            setVATVerificationUrl("");
          }
        } else {
          NotificationWithIcon(
            "error",
            t("something_went_wrong"),
            response.data.message,
          );
        }
      })
      .catch((info) => {
        console.log("API Failed:", info.response);
        NotificationWithIcon(
          "error",
          t("something_went_wrong"),
          info.response.data.data.errors.name,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {/* buttons section */}
      <div style={{ marginBottom: "20px", textAlign: "center" }}>
        {authUser.type !== USER_TYPE_ADMIN_STAFF && (
          <UpdateCompanyAdminStaff
            company={company}
            reloadCallback={requestReloadCallback}
          />
        )}
        {authUser.type !== USER_TYPE_ADMIN_STAFF && (
          <UpdateReseller
            company={company}
            reloadCallback={requestReloadCallback}
          />
        )}
        <Button type="primary" style={{ marginRight: "20px" }}>
          <a
            href="https://zatca.gov.sa/en/eServices/Pages/TaxpayerLookup.aspx"
            target="_blank"
            rel="noreferrer"
          >
            {t("visit_website_for_verification")}
          </a>
        </Button>
        <Button
          type="primary"
          onClick={showEditModel}
          style={{ marginRight: "20px" }}
        >
          {t("edit_company_details")}
        </Button>

        {company.name &&
          company.business_type?.id &&
          company.business_type_verified &&
          company.cr &&
          company.cr_certificate &&
          company.cr_verification &&
          hasCompleteCompanyBusinessAddress(company) &&
          company.status !== COMPANY_STATUS_KYC &&
          (company.is_active ? (
            <Button
              type="button"
              onClick={() => handleDeactivate(company.id)}
              style={{
                padding: "2px 10px",
                backgroundColor: "#ff0000",
                color: "#ffffff",
                marginRight: "20px",
              }}
            >
              <LockOutlined title={t("company_deactivate")} />{" "}
              {t("company_deactivate")}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => handleActivate(company.id)}
              style={{
                padding: "2px 10px",
                backgroundColor: "#087c08",
                color: "#ffffff",
                marginRight: "20px",
              }}
            >
              <UnlockOutlined title={t("company_activate")} />{" "}
              {t("company_activate")}
            </Button>
          ))}

        {company.status !== 1 && (
          <Popconfirm
            title={t("warning_delete")}
            onConfirm={() => handleDeleteCompany(company.id)}
            okText={t("yes")}
            cancelText={t("no")}
            placement="topLeft"
          >
            <Button
              type="button"
              style={{
                padding: "2px 10px",
                backgroundColor: "#ff0000",
                color: "#ffffff",
              }}
            >
              <DeleteOutlined title={t("company_deleted")} />
            </Button>
          </Popconfirm>
        )}
      </div>

      {/* show company details */}
      <Spin spinning={loading}>
        <Row>
          <Col xs={24} sm={24} md={24} lg={1} xl={2} xxl={4}></Col>
          <Col xs={24} sm={24} md={24} lg={22} xl={20} xxl={16}>
            <div
              style={{
                border: "1px solid #6e6e6e",
                padding: "100px 30px 30px",
                marginTop: "90px",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "50%",
                  marginLeft: "-86px",
                  marginTop: "-86px",
                  background: "#ffffff",
                  border: "1px solid #6e6e6e",
                  padding: "10px",
                  borderRadius: "50%",
                }}
              >
                <img
                  src={company.logo || "/images/image-not-found.png"}
                  alt={t("business_logo")}
                  width={150}
                  style={{
                    borderRadius: "50%",
                    height: "150px",
                    objectFit: "contain",
                  }}
                />
              </div>
              <Row gutter={[20, 20]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("business_name")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.name || "-"}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("subdomain")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.subdomain || EMPTY_VALUE}
                    {company.subdomain && TENANT_BASE_DOMAIN ? (
                      <Button
                        type="link"
                        href={`https://${company.subdomain}.${TENANT_BASE_DOMAIN}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: "0 8px" }}
                      >
                        {t("open_store_portal")}
                      </Button>
                    ) : null}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("business_type")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_type ? company.business_type.name : "-"}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("business_type_verified")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {Number(company.business_type_verified) === 1 ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("cr_number")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.cr || "-"}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("cr_certificate")}
                  </div>
                  <div
                    style={
                      company.cr_certificate
                        ? { border: "1px solid #d9d9d9", padding: "5px" }
                        : { border: "1px solid #d9d9d9", padding: "10px" }
                    }
                  >
                    {company.cr_certificate ? (
                      <Button
                        title={t("preview_cr_certificate")}
                        target="_blank"
                        href={company.cr_certificate}
                        type="link"
                      >
                        <EyeOutlined /> {t("preview_cr_certificate")}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("cr_verification")}
                  </div>
                  <div
                    style={
                      company.cr_verification
                        ? { border: "1px solid #d9d9d9", padding: "5px" }
                        : { border: "1px solid #d9d9d9", padding: "10px" }
                    }
                  >
                    {company.cr_verification ? (
                      <Button
                        title={t("preview_cr_verification")}
                        target="_blank"
                        href={company.cr_verification}
                        type="link"
                      >
                        <EyeOutlined /> {t("preview_cr_verification")}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>

                {company.is_vat_exempt ? (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                      {t("is_vat_exempt")}
                    </div>
                    <div
                      style={{ border: "1px solid #d9d9d9", padding: "10px" }}
                    >
                      {company.is_vat_exempt ? (
                        <Tag color="green">{t("yes")}</Tag>
                      ) : (
                        <Tag color="red">{t("no")}</Tag>
                      )}
                    </div>
                  </Col>
                ) : (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                      {t("vat_number")}
                    </div>
                    <div
                      style={{ border: "1px solid #d9d9d9", padding: "10px" }}
                    >
                      {company.vat || "-"}
                    </div>
                  </Col>
                )}

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("vat_certificate")}
                  </div>
                  <div
                    style={
                      company.vat_certificate
                        ? { border: "1px solid #d9d9d9", padding: "5px" }
                        : { border: "1px solid #d9d9d9", padding: "10px" }
                    }
                  >
                    {company.vat_certificate ? (
                      <Button
                        title={t("preview_vat_certificate")}
                        target="_blank"
                        href={company.vat_certificate}
                        type="link"
                      >
                        <EyeOutlined /> {t("preview_vat_certificate")}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("vat_verification")}
                  </div>
                  <div
                    style={
                      company.vat_verification
                        ? { border: "1px solid #d9d9d9", padding: "5px" }
                        : { border: "1px solid #d9d9d9", padding: "10px" }
                    }
                  >
                    {company.vat_verification ? (
                      <Button
                        title={t("preview_vat_verification")}
                        target="_blank"
                        href={company.vat_verification}
                        type="link"
                      >
                        <EyeOutlined /> {t("preview_vat_verification")}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("business_name_arabic")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_arabic_name || EMPTY_VALUE}
                  </div>
                </Col>

                <Col span={24}>
                  <h2 style={{ marginBottom: "4px" }}>
                    {t("business_address_details")}
                  </h2>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("country")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_country || EMPTY_VALUE}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("postal_code")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_postal_code || EMPTY_VALUE}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("col_region")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_region?.name_en || EMPTY_VALUE}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("city")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_city?.name_en || EMPTY_VALUE}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("address")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.business_address || EMPTY_VALUE}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("allow_round_off")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.round_off ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    {t("show_calories")}
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.is_calories ? (
                      <Tag color="green">{t("yes")}</Tag>
                    ) : (
                      <Tag color="red">{t("no")}</Tag>
                    )}
                  </div>
                </Col>

                {company.preferred_contact_time && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                      {t("preferred_contact_time")}
                    </div>
                    <div
                      style={{ border: "1px solid #d9d9d9", padding: "10px" }}
                    >
                      {company.preferred_contact_time || "-"}
                    </div>
                  </Col>
                )}

                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div style={{ fontWeight: "bold", paddingBottom: "10px" }}>
                    Master User Contact
                  </div>
                  <div style={{ border: "1px solid #d9d9d9", padding: "10px" }}>
                    {company.owner_contact || "-"}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col xs={24} sm={24} md={24} lg={1} xl={2} xxl={4}></Col>
        </Row>
      </Spin>

      {/* edit company details */}
      <Modal
        title={t("edit_company_details")}
        open={editModal}
        onCancel={() => {
          setEditModal(false);
        }}
        onOk={onFinishConfigForm}
        width={1000}
        style={{
          top: 20,
        }}
        footer={[
          <Button
            key="back"
            loading={loading}
            onClick={() => setEditModal(false)}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={onFinishConfigForm}
          >
            {t("update")}
          </Button>,
        ]}
      >
        <Spin spinning={loading}>
          <Form
            name="store-configuration-form"
            layout="vertical"
            onFinish={onFinishConfigForm}
            style={{ width: "100%" }}
            form={storeConfigurationForm}
          >
            <Form.Item name="id" hidden>
              <input />
            </Form.Item>
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_name"
                  label={t("business_name")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_name_required"),
                      whitespace: true,
                    },
                  ]}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_arabic_name"
                  label={t("business_name_arabic")}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="subdomain"
                  label={t("subdomain")}
                  normalize={(value) => value?.toLowerCase()}
                  extra={
                    subdomain
                      ? `${t("store_url_preview")}: ${subdomain}${
                          TENANT_BASE_DOMAIN ? `.${TENANT_BASE_DOMAIN}` : ""
                        }`
                      : null
                  }
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_type_id"
                  label={t("business_type")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_type_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select>
                    {businessTypes.map((business) => (
                      <Select.Option key={business.id} value={business.id}>
                        {appConfig.direction === "ltr"
                          ? business.name
                          : business.name_ar}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_type_verified"
                  label={t("business_type_verified")}
                >
                  <Radio.Group buttonStyle="solid" key="business-type-verified">
                    <Radio.Button key="business-type-verified-true" value={1}>
                      {t("yes")}
                    </Radio.Button>
                    <Radio.Button key="business-type-verified-false" value={0}>
                      {t("no")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="cr"
                  label={t("cr_number")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_cr_number_required"),
                    },
                    {
                      min: 10,
                      message: t("validate_cr_number_exact_limit"),
                    },
                    {
                      max: 10,
                      message: t("validate_cr_number_exact_limit"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input maxLength={10} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="is_vat_exempt"
                  valuePropName="checked"
                  style={{ marginTop: "30px" }}
                >
                  <Checkbox
                    onChange={(e) => {
                      setVatExempt(e.target.checked);
                    }}
                  >
                    {t("is_vat_exempt")}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                {!vatExempt && (
                  <Form.Item
                    name="vat"
                    label={t("vat_number")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_vat_number_required"),
                      },
                      {
                        min: 15,
                        message: t("validate_vat_number_exact_limit"),
                      },
                      {
                        max: 15,
                        message: t("validate_vat_number_exact_limit"),
                      },
                    ]}
                    hasFeedback
                  >
                    <Input maxLength={15} />
                  </Form.Item>
                )}
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="ant-col ant-form-item-label">
                  <label
                    className="ant-form-item-required"
                    title={t("cr_certificate")}
                  >
                    {t("cr_certificate")}
                  </label>
                </div>
                <div>
                  <Upload
                    name="cr_certificate"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={beforeUploadCertificate}
                    onChange={(info) => handleChange(info, "cr_certificate")}
                    customRequest={(data) =>
                      uploadCustomRequest(data, "cr_certificate")
                    }
                  >
                    {crCertificateUrl ? (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>{t("change")}</div>
                      </div>
                    ) : (
                      uploadButton("cr_certificate")
                    )}
                  </Upload>
                  {crCertificateUrl && (
                    <Space>
                      <Button
                        type="primary"
                        href={crCertificateUrl}
                        target="_blank"
                      >
                        <EyeOutlined />
                      </Button>
                      {authUser.type !== USER_TYPE_ADMIN_STAFF && (
                        <Popconfirm
                          title={t("warning_delete")}
                          onConfirm={() =>
                            handleDeleteUpload(company.id, "cr_certificate")
                          }
                          okText={t("yes")}
                          cancelText={t("no")}
                          placement="topLeft"
                        >
                          <Button danger>
                            <DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="ant-col ant-form-item-label">
                  <label title={t("cr_verification")}>
                    {t("cr_verification")}
                  </label>
                </div>
                <div>
                  <Upload
                    name="cr_verification"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={beforeUploadCertificate}
                    onChange={(info) => handleChange(info, "cr_verification")}
                    customRequest={(data) =>
                      uploadCustomRequest(data, "cr_verification")
                    }
                  >
                    {crVerificationUrl ? (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>{t("change")}</div>
                      </div>
                    ) : (
                      uploadButton("cr_verification")
                    )}
                  </Upload>

                  {crVerificationUrl && (
                    <Space>
                      <Button
                        type="primary"
                        href={crVerificationUrl}
                        target="_blank"
                      >
                        <EyeOutlined />
                      </Button>
                      {authUser.type !== USER_TYPE_ADMIN_STAFF && (
                        <Popconfirm
                          title={t("warning_delete")}
                          onConfirm={() =>
                            handleDeleteUpload(company.id, "cr_verification")
                          }
                          okText={t("yes")}
                          cancelText={t("no")}
                          placement="topLeft"
                        >
                          <Button danger>
                            <DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="ant-col ant-form-item-label">
                  <label
                    className="ant-form-item-required"
                    title={t("business_logo")}
                  >
                    {t("business_logo")}
                  </label>
                </div>
                <Upload
                  name="logo"
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={(info) => handleChange(info, "logo")}
                  customRequest={(data) => uploadCustomRequest(data, "logo")}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={t("business_logo")}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    uploadButton("logo")
                  )}
                </Upload>

                {logoUrl && authUser.type !== USER_TYPE_ADMIN_STAFF && (
                  <Popconfirm
                    title={t("warning_delete")}
                    onConfirm={() => handleDeleteUpload(company.id, "logo")}
                    okText={t("yes")}
                    cancelText={t("no")}
                    placement="topLeft"
                  >
                    <Button danger>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                )}
              </Col>

              {!vatExempt && (
                <>
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div className="ant-col ant-form-item-label">
                      <label
                        className="ant-form-item-required"
                        title={t("vat_certificate")}
                      >
                        {t("vat_certificate")}
                      </label>
                    </div>
                    <div>
                      <Upload
                        name="vat_certificate"
                        listType="picture-card"
                        showUploadList={false}
                        beforeUpload={beforeUploadCertificate}
                        onChange={(info) =>
                          handleChange(info, "vat_certificate")
                        }
                        customRequest={(data) =>
                          uploadCustomRequest(data, "vat_certificate")
                        }
                      >
                        {vatCertificateUrl ? (
                          <div>
                            <UploadOutlined />
                            <div style={{ marginTop: 8 }}>{t("change")}</div>
                          </div>
                        ) : (
                          uploadButton("vat_certificate")
                        )}
                      </Upload>

                      {vatCertificateUrl && (
                        <Space>
                          <Button
                            type="primary"
                            href={vatCertificateUrl}
                            target="_blank"
                          >
                            <EyeOutlined />
                          </Button>
                          {authUser.type !== USER_TYPE_ADMIN_STAFF && (
                            <Popconfirm
                              title={t("warning_delete")}
                              onConfirm={() =>
                                handleDeleteUpload(
                                  company.id,
                                  "vat_certificate",
                                )
                              }
                              okText={t("yes")}
                              cancelText={t("no")}
                              placement="topLeft"
                            >
                              <Button danger>
                                <DeleteOutlined />
                              </Button>
                            </Popconfirm>
                          )}
                        </Space>
                      )}
                    </div>
                  </Col>

                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div className="ant-col ant-form-item-label">
                      <label title={t("vat_verification")}>
                        {t("vat_verification")}
                      </label>
                    </div>
                    <div>
                      <Upload
                        name="vat_verification"
                        listType="picture-card"
                        showUploadList={false}
                        beforeUpload={beforeUploadCertificate}
                        onChange={(info) =>
                          handleChange(info, "vat_verification")
                        }
                        customRequest={(data) =>
                          uploadCustomRequest(data, "vat_verification")
                        }
                      >
                        {vatVerificationUrl ? (
                          <div>
                            <UploadOutlined />
                            <div style={{ marginTop: 8 }}>{t("change")}</div>
                          </div>
                        ) : (
                          uploadButton("vat_verification")
                        )}
                      </Upload>

                      {vatVerificationUrl && (
                        <Space>
                          <Button
                            type="primary"
                            href={vatVerificationUrl}
                            target="_blank"
                          >
                            <EyeOutlined />
                          </Button>
                          {authUser.type !== USER_TYPE_ADMIN_STAFF && (
                            <Popconfirm
                              title={t("warning_delete")}
                              onConfirm={() =>
                                handleDeleteUpload(
                                  company.id,
                                  "vat_verification",
                                )
                              }
                              okText={t("yes")}
                              cancelText={t("no")}
                              placement="topLeft"
                            >
                              <Button danger>
                                <DeleteOutlined />
                              </Button>
                            </Popconfirm>
                          )}
                        </Space>
                      )}
                    </div>
                  </Col>
                </>
              )}

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item name="round_off" label={t("allow_round_off")}>
                  <Radio.Group buttonStyle="solid" key="allow-round-off">
                    <Radio.Button key="allow-round-off-true" value={true}>
                      {t("yes")}
                    </Radio.Button>
                    <Radio.Button key="allow-round-off-false" value={false}>
                      {t("no")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item name="is_calories" label={t("show_calories")}>
                  <Radio.Group buttonStyle="solid" key="show-calories">
                    <Radio.Button key="show-calories-true" value={true}>
                      {t("yes")}
                    </Radio.Button>
                    <Radio.Button key="show-calories-false" value={false}>
                      {t("no")}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={24}>
                <h2 style={{ marginBottom: "4px" }}>
                  {t("business_address_details")}
                </h2>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_country"
                  label={t("country")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_country_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Input disabled />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_postal_code"
                  label={t("postal_code")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_postal_code_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_region_id"
                  label={t("col_region")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_region_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    showSearch
                    onChange={handleRegionSelect}
                    options={regions.map((region) => ({
                      label:
                        appConfig.direction === "ltr"
                          ? region.name_en
                          : region.name_ar,
                      value: region.id,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_city_id"
                  label={t("city")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_city_required"),
                    },
                  ]}
                  hasFeedback
                >
                  <Select
                    showSearch
                    options={cities.map((city) => ({
                      label:
                        appConfig.direction === "ltr"
                          ? city.name_en
                          : city.name_ar,
                      value: city.id,
                    }))}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <Form.Item
                  name="business_address"
                  label={t("address")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_business_address_required"),
                      whitespace: true,
                    },
                  ]}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default CustomerInfo;
